import { useMemo } from 'react';

import { snakeToCamel } from '@mint-lib/api';
import { useFlags } from '@mint-lib/routing-context';
import camelcase from 'camelcase';

import { getBuilderColumnObjects } from '../constants/customColumnObjects.js';
import {
  beyondWayColumnsUnderFf,
  beyondWayColumnsUnderFfPart2,
  Columns,
  predictiveColumns,
} from '../constants/myCampaigns.js';
import { BuilderTableViewColumn } from '../types/builderTableView.js';
import { useBuilderTableViewDataSelector } from './useGetBuilderTableViewData.js';
import { usePredictiveDataSelector } from './useGetPredictivePlanningData.js';

const usePreparedBuilderTableColumns = () => {
  const ff = useFlags('@mint/arm', [
    'mdmColumnsForBuyingPlan',
    'beyondWayColumnsBuyingPlan',
    'beyondWayColumnsBuyingPlanPart2',
  ]);
  const { data: initialColumnsRaw } =
    useBuilderTableViewDataSelector('columns');
  const { data: columnWidths } = useBuilderTableViewDataSelector('columnWidth');
  const { data: predictiveData } = usePredictiveDataSelector('mediaRows');

  const initialColumns = useMemo<BuilderTableViewColumn[]>(() => {
    if (!initialColumnsRaw) {
      return [];
    }

    const blockedBeyondWay = !ff.beyondWayColumnsBuyingPlan.enabled;
    const blockedBeyondWayPart2 = !ff.beyondWayColumnsBuyingPlanPart2.enabled;

    return initialColumnsRaw.filter((column) => {
      const camelName = snakeToCamel(column.name);

      if (blockedBeyondWay && beyondWayColumnsUnderFf.includes(camelName)) {
        return false;
      }

      if (
        blockedBeyondWayPart2 &&
        beyondWayColumnsUnderFfPart2.includes(camelName)
      ) {
        return false;
      }

      return true;
    });
  }, [
    initialColumnsRaw,
    ff.beyondWayColumnsBuyingPlan.enabled,
    ff.beyondWayColumnsBuyingPlanPart2.enabled,
  ]);

  const preparedColumns: BuilderTableViewColumn[] = useMemo(() => {
    return [
      { name: Columns.EXPANDER, label: '' },
      ...initialColumns,
      { name: Columns.MASK, label: '' },
      { name: Columns.ACTIONS_MENU, label: '' },
    ];
  }, [initialColumns]);

  const columnNames = useMemo(() => {
    const order = preparedColumns.map((col) =>
      ff.mdmColumnsForBuyingPlan.enabled
        ? snakeToCamel(col.name)
        : camelcase(col.name),
    );

    if (predictiveData) {
      const budgetColumnIndex = order.indexOf(Columns.BUDGET);

      const predictiveColumnsWithMdm = predictiveColumns.map((col) =>
        ff.mdmColumnsForBuyingPlan.enabled ? snakeToCamel(col) : camelcase(col),
      );

      const orderWithPredictiveColumns = [
        ...order.slice(0, budgetColumnIndex + 1),
        ...predictiveColumnsWithMdm,
        ...order.slice(budgetColumnIndex + 1),
      ];

      return orderWithPredictiveColumns;
    }

    return order;
  }, [preparedColumns, predictiveData, ff.mdmColumnsForBuyingPlan.enabled]);

  const columnObjects = useMemo(() => {
    return getBuilderColumnObjects(preparedColumns, columnWidths);
  }, [preparedColumns, columnWidths]);

  return { columnNames, columnObjects, initialColumns };
};

export default usePreparedBuilderTableColumns;
